import { useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import appCss from "../styles.css?url";
import { loadPostHog, capturePageview, capture } from "@/lib/analytics";
import { GTM_ID, pushToDataLayer } from "@/lib/analytics/gtm";
import { attachCtaTracking } from "@/lib/analytics/cta-tracking";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { NotFoundPage } from "@/components/not-found-page";
import { organization, people, serializeJsonLd, website } from "@/lib/structured-data";
import { SITE_URL, BRAND_NAME, TWITTER_HANDLE, DEFAULT_OG_ALT } from "@/lib/seo-constants";

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();

  useEffect(() => {
    capture("page_error", { error_name: error.name, error_message: error.message });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: `${BRAND_NAME} — Algorithmic Marketing on Autopilot` },
      {
        name: "description",
        content:
          "Axy.digital is the autonomous marketing engine that detects market demand and turns it into GEO, LinkedIn, and X campaigns automatically.",
      },
      { property: "og:site_name", content: BRAND_NAME },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "en_US" },
      {
        property: "og:image",
        content: `${SITE_URL}/og/default.png`,
      },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:type", content: "image/png" },
      { property: "og:image:alt", content: DEFAULT_OG_ALT },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: TWITTER_HANDLE },
      { name: "twitter:creator", content: TWITTER_HANDLE },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "preconnect", href: "https://www.googletagmanager.com" },
      { rel: "icon", href: "/favicon/favicon.ico", sizes: "any" },
      {
        rel: "icon",
        href: "/favicon/favicon-32x32.png",
        type: "image/png",
        sizes: "32x32",
      },
      {
        rel: "icon",
        href: "/favicon/favicon-16x16.png",
        type: "image/png",
        sizes: "16x16",
      },
      {
        rel: "apple-touch-icon",
        href: "/favicon/apple-touch-icon.png",
        sizes: "180x180",
      },
      { rel: "manifest", href: "/favicon/site.webmanifest" },
      {
        rel: "alternate",
        type: "application/rss+xml",
        title: "Axy.digital Blog",
        href: "/blog/rss.xml",
      },
      {
        rel: "alternate",
        type: "application/rss+xml",
        title: "Axy.digital Case Studies",
        href: "/case-studies/rss.xml",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: serializeJsonLd(organization()),
      },
      {
        type: "application/ld+json",
        children: serializeJsonLd(website()),
      },
      ...people().map((p) => ({
        type: "application/ld+json",
        children: serializeJsonLd(p),
      })),
      ...(GTM_ID
        ? [
            // Google Tag Manager container loader. Loads async after the page's
            // `load` event: GTM and the marketing tags it pulls stay off the
            // critical render path (LCP is already painted), but it fires for
            // every visitor — including quick bounces that never interact, which
            // the old interaction-gated loader silently dropped. No consent
            // gating: analytics defaults to granted (no CMP currently in use).
            //
            // The `w.__gtmInit` guard is load-bearing: TanStack's <Asset> effect
            // removes and re-appends inline head scripts to `document.head` on
            // re-render, and re-inserting a <script> node re-executes it. Without
            // the guard every re-execution appends another `gtm.js`, so the
            // container loads (and GA4 double-counts) once per render.
            {
              id: "gtm-loader",
              children: `(function(w,d,s,l,i){w[l]=w[l]||[];if(w.__gtmInit)return;w.__gtmInit=1;function load(){w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s);j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i;f.parentNode.insertBefore(j,f);}if(d.readyState==='complete'){load();}else{w.addEventListener('load',load,{once:true});}})(window,document,'script','dataLayer','${GTM_ID}');`,
            },
          ]
        : []),
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundPage,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en" className="dark">
      <head>
        <HeadContent />
      </head>
      <body>
        {GTM_ID && (
          <noscript>
            <iframe
              src={`https://www.googletagmanager.com/ns.html?id=${GTM_ID}`}
              height="0"
              width="0"
              style={{ display: "none", visibility: "hidden" }}
              title="Google Tag Manager"
            />
          </noscript>
        )}
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const hideGlobalHeader = pathname.startsWith("/lp/");
  // Prefix match, so every arm of a `/lp/schedule*` A/B test loses the footer:
  // an exact list silently leaves the new variant with a footer the control
  // doesn't have, turning a headline test into a two-variable one.
  const hideGlobalFooter = pathname.startsWith("/lp/schedule");
  const router = useRouter();

  useEffect(() => {
    // PostHog is loaded lazily after the window `load` event, mirroring the
    // GTM loader above, so the SDK never sits on the critical render path.
    if (document.readyState === "complete") {
      void loadPostHog();
      return;
    }
    const handleLoad = () => void loadPostHog();
    window.addEventListener("load", handleLoad, { once: true });
    return () => window.removeEventListener("load", handleLoad);
  }, []);

  // Delegated listener for the outbound sign-in/sign-up CTAs, which are plain
  // anchors spread across every landing page.
  useEffect(() => attachCtaTracking(), []);

  useEffect(() => {
    // PostHog runs with `capture_pageview: false`, and `router.subscribe` only
    // sees resolves that happen *after* this effect mounts — the entry route
    // already resolved during hydration. So the initial `$pageview`, the one
    // carrying the UTM params, has to be captured by hand here.
    //
    // GTM is deliberately excluded from this first call: its container fires
    // its own pageview tag on load, and pushing `spa_pageview` too would
    // double-count the entry in GA4.
    const initial = router.state.location;
    const initialPath = initial.pathname + initial.searchStr;
    capturePageview(initialPath);

    // The entry route can *also* resolve just after this effect mounts, which
    // would emit the pageview above a second time. Suppress that one resolve
    // only — a later navigation back to the same path is a real pageview and
    // must still be counted (GTM counted it before PostHog was introduced).
    let pendingInitialResolve: string | null = initialPath;

    // SPA navigations don't reload the page, so notify analytics on every
    // route resolve. GTM's GA4 pageview tag fires off the dataLayer event;
    // PostHog gets a manual `$pageview`. The two are independent — PostHog is
    // NOT gated on GTM, so either can be enabled on its own.
    return router.subscribe("onResolved", ({ toLocation }) => {
      const path = toLocation.pathname + toLocation.searchStr;
      if (pendingInitialResolve !== null) {
        const isDuplicateEntry = pendingInitialResolve === path;
        pendingInitialResolve = null;
        if (isDuplicateEntry) return;
      }
      pushToDataLayer("spa_pageview", {
        page_path: path,
        page_location: window.location.href,
        page_title: document.title,
      });
      capturePageview(path);
    });
  }, [router]);

  return (
    <QueryClientProvider client={queryClient}>
      <div className="flex min-h-screen flex-col">
        {!hideGlobalHeader && <SiteHeader />}
        <main className="flex-1">
          <Outlet />
        </main>
        {!hideGlobalFooter && <SiteFooter />}
      </div>
    </QueryClientProvider>
  );
}
